import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, Plus, ShieldCheck, Trash2, Users } from 'lucide-react'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { useCreateRole, useDeleteRole, useRoles, useUpdateRole } from '@/hooks/useRoles'
import { MODULE_GROUPS } from '@/lib/permission-labels'
import type { Permission } from '@/lib/permissions'
import type { TenantRole } from '@/types/role'

function extractApiMessage(err: unknown, fallback: string) {
  if (isAxiosError(err)) {
    return (
      (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message ?? fallback
    )
  }
  return fallback
}

type EditorMode =
  | { kind: 'edit'; role: TenantRole }
  | { kind: 'new'; duplicateFrom?: TenantRole }

export function RolesTab() {
  const { data: roles, isLoading } = useRoles()
  const [mode, setMode] = useState<EditorMode | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TenantRole | null>(null)
  const deleteMutation = useDeleteRole()

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>
  }

  if (mode) {
    return <RoleEditor mode={mode} onClose={() => setMode(null)} roles={roles ?? []} />
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(`Função "${deleteTarget.name}" excluída`)
      setDeleteTarget(null)
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao excluir função'))
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Funções definem o que cada usuário pode fazer. Os 3 presets (Administrador,
          Financeiro, Operação) são padrão do sistema — você pode ajustar as permissões mas
          não pode excluir nem renomear. Crie funções customizadas para perfis específicos
          da sua operação.
        </p>
        <Button size="sm" onClick={() => setMode({ kind: 'new' })}>
          <Plus size={14} />
          Nova função
        </Button>
      </div>

      <ul className="space-y-3">
        {(roles ?? []).map((role) => (
          <li key={role.id}>
            <RoleCard
              role={role}
              onEdit={() => setMode({ kind: 'edit', role })}
              onDuplicate={() => setMode({ kind: 'new', duplicateFrom: role })}
              onDelete={() => setDeleteTarget(role)}
            />
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Excluir função "${deleteTarget?.name}"?`}
        description="Esta ação é irreversível. A função não poderá ser usada novamente para convites ou edição de usuários."
        confirmLabel="Excluir"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function RoleCard({
  role,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  readonly role: TenantRole
  readonly onEdit: () => void
  readonly onDuplicate: () => void
  readonly onDelete: () => void
}) {
  const canDelete = !role.isSystem && role.userCount === 0
  return (
    <div className="flex items-start justify-between rounded-lg border border-border bg-background p-4">
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">{role.name}</h3>
          {role.isSystem && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              Padrão
            </span>
          )}
        </div>
        {role.description && (
          <p className="text-xs text-muted-foreground">{role.description}</p>
        )}
        <div className="flex items-center gap-4 pt-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Users size={12} />
            {role.userCount} {role.userCount === 1 ? 'usuário' : 'usuários'}
          </span>
          <span>{role.permissions.length} permissões</span>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onDuplicate}>
          Duplicar
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit}>
          Editar
        </Button>
        {!role.isSystem && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            disabled={!canDelete}
            title={canDelete ? 'Excluir' : 'Não é possível excluir: existem usuários associados a esta função.'}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  )
}

function RoleEditor({
  mode,
  onClose,
  roles,
}: {
  readonly mode: EditorMode
  readonly onClose: () => void
  readonly roles: ReadonlyArray<TenantRole>
}) {
  const isNew = mode.kind === 'new'
  const initial = useMemo<TenantRole | null>(() => {
    if (mode.kind === 'edit') return mode.role
    if (mode.kind === 'new' && mode.duplicateFrom) {
      const src = mode.duplicateFrom
      return {
        ...src,
        id: '',
        name: `${src.name} (cópia)`,
        isSystem: false,
        preset: null,
        userCount: 0,
      }
    }
    return null
  }, [mode])

  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [permissions, setPermissions] = useState<Set<string>>(
    () => new Set(initial?.permissions ?? []),
  )

  const update = useUpdateRole(mode.kind === 'edit' ? mode.role.id : '')
  const create = useCreateRole()
  const isPending = update.isPending || create.isPending

  const togglePermission = (key: Permission) => {
    setPermissions((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const setModuleAll = (keys: readonly Permission[], on: boolean) => {
    setPermissions((prev) => {
      const next = new Set(prev)
      for (const k of keys) {
        if (on) next.add(k)
        else next.delete(k)
      }
      return next
    })
  }

  const handleSave = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Nome é obrigatório')
      return
    }
    if (isNew) {
      const nameConflict = roles.some(
        (r) => r.name.toLowerCase() === trimmedName.toLowerCase(),
      )
      if (nameConflict) {
        toast.error('Já existe uma função com este nome')
        return
      }
    }
    try {
      const payload = {
        name:
          mode.kind === 'edit' && mode.role.isSystem ? mode.role.name : trimmedName,
        description: description.trim() || undefined,
        permissions: Array.from(permissions),
      }
      if (mode.kind === 'edit') {
        await update.mutateAsync(payload)
        toast.success('Função atualizada')
      } else {
        await create.mutateAsync(payload)
        toast.success('Função criada')
      }
      onClose()
    } catch (err) {
      toast.error(extractApiMessage(err, 'Erro ao salvar função'))
    }
  }

  const headerTitle = isNew ? 'Nova função' : 'Editar função'
  let headerSubtitle: string | null = null
  if (mode.kind === 'edit' && mode.role.isSystem) {
    headerSubtitle = 'Função padrão do sistema: nome não pode ser alterado.'
  } else if (mode.kind === 'new' && mode.duplicateFrom) {
    headerSubtitle = `Duplicada de "${mode.duplicateFrom.name}". Ajuste antes de salvar.`
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={onClose} disabled={isPending}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h2 className="text-base font-semibold text-foreground">{headerTitle}</h2>
          {headerSubtitle && <p className="text-xs text-muted-foreground">{headerSubtitle}</p>}
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-background p-4">
        <div>
          <label htmlFor="role-name" className="text-xs font-medium text-muted-foreground">
            Nome
          </label>
          <input
            id="role-name"
            type="text"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={(mode.kind === 'edit' && mode.role.isSystem) || isPending}
            maxLength={60}
            placeholder="Ex: Operação Sênior"
          />
        </div>
        <div>
          <label htmlFor="role-description" className="text-xs font-medium text-muted-foreground">
            Descrição (opcional)
          </label>
          <textarea
            id="role-description"
            rows={2}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isPending}
            maxLength={280}
            placeholder="O que esta função faz na operação?"
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground">Permissões</h3>

        {MODULE_GROUPS.map((group) => {
          const keys = group.permissions.map((p) => p.key)
          const grantedInGroup = keys.filter((k) => permissions.has(k)).length
          const allOn = grantedInGroup === keys.length
          const noneOn = grantedInGroup === 0

          return (
            <div key={group.module} className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">{group.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {grantedInGroup} de {keys.length} permissões
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setModuleAll(keys, true)}
                    disabled={allOn || isPending}
                  >
                    Marcar tudo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setModuleAll(keys, false)}
                    disabled={noneOn || isPending}
                  >
                    Desmarcar tudo
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {group.permissions.map((p) => (
                  <label
                    key={p.key}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-transparent p-2 text-sm hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-input"
                      checked={permissions.has(p.key)}
                      onChange={() => togglePermission(p.key)}
                      disabled={isPending}
                    />
                    <span className="flex-1">
                      <span className="block text-foreground">{p.label}</span>
                      {p.description && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {p.description}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={isPending}>
          {(() => {
            if (isPending) return 'Salvando…'
            return isNew ? 'Criar função' : 'Salvar alterações'
          })()}
        </Button>
      </div>
    </div>
  )
}
